import React, { useState, useMemo } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { boneParentMap } from "../data/boneTree";

interface BoneTreeNode {
  value: string;
  label: string;
  children: BoneTreeNode[];
}

// 将父子关系映射转换为树形结构
function buildBoneTree(): BoneTreeNode[] {
  const childrenMap: Record<string, string[]> = {};

  Object.keys(boneParentMap).forEach((bone) => {
    childrenMap[bone] = [];
  });

  Object.entries(boneParentMap).forEach(([bone, parent]) => {
    if (parent !== null) {
      if (!childrenMap[parent]) {
        childrenMap[parent] = [];
      }
      childrenMap[parent].push(bone);
    }
  });

  const rootNodes = Object.entries(boneParentMap)
    .filter(([, parent]) => parent === null)
    .map(([bone]) => bone);

  function buildNode(bone: string): BoneTreeNode {
    return {
      value: bone,
      label: bone,
      children: (childrenMap[bone] || []).map(buildNode),
    };
  }

  return rootNodes.map(buildNode);
}

// 树节点组件
function TreeNode({
  node,
  selectedValue,
  onSelect,
  level = 0,
}: {
  node: BoneTreeNode;
  selectedValue: string;
  onSelect: (value: string) => void;
  level?: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children?.length > 0;

  return (
    <div>
      <div
        className={cn(
          "flex items-center py-1.5 px-2 cursor-pointer rounded-sm hover:bg-accent",
          selectedValue === node.value &&
            "bg-accent text-accent-foreground font-medium",
        )}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
        onClick={() => onSelect(node.value)}
      >
        {hasChildren ? (
          <button
            className="mr-1 hover:bg-accent/50 rounded p-0.5"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            type="button"
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : (
          <span className="w-6" />
        )}
        <span className="flex-1 text-sm">{node.label}</span>
      </div>
      {hasChildren &&
        expanded &&
        node.children.map((child) => (
          <TreeNode
            key={child.value}
            node={child}
            selectedValue={selectedValue}
            onSelect={onSelect}
            level={level + 1}
          />
        ))}
    </div>
  );
}

interface BoneTreePanelProps {
  value: string;
  onChange: (value: string) => void;
}

export function BoneTreePanel({ value, onChange }: BoneTreePanelProps) {
  const treeData = useMemo(() => buildBoneTree(), []);

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 pt-4">
        <CardTitle className="text-base">Bone</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto p-2">
        {treeData.map((node) => (
          <TreeNode
            key={node.value}
            node={node}
            selectedValue={value || ""}
            onSelect={onChange}
          />
        ))}
      </CardContent>
    </Card>
  );
}
